/**
 * Invoicing API surface (M1).
 *
 * Money is a string here for the same reason it is one in the orders client: the API sends
 * a Postgres NUMERIC as text because it does not survive a double, and the only place it
 * becomes a number is `formatMoney`, at the last moment before somebody reads it. An
 * invoice is the document a customer disputes, so a cent lost to floating point here is
 * worse than anywhere else in the app.
 */

import { API_PREFIX, apiBlob, apiFetch, apiGet, type Page } from '@/api/client'
import type { ExportFormat } from '@/components/ExportButton'

export const INVOICE_STATUSES = [
  'draft',
  'issued',
  'partially_paid',
  'paid',
  'overdue',
  'void',
] as const

export type InvoiceStatus = (typeof INVOICE_STATUSES)[number]

export const STATUS_LABELS: Record<InvoiceStatus, string> = {
  draft: 'Draft',
  issued: 'Issued',
  partially_paid: 'Part paid',
  paid: 'Paid',
  overdue: 'Overdue',
  void: 'Void',
}

export const PAYMENT_METHODS = ['cash', 'card', 'bank_transfer', 'cheque', 'gateway'] as const

export type PaymentMethod = (typeof PAYMENT_METHODS)[number]

export const METHOD_LABELS: Record<PaymentMethod, string> = {
  cash: 'Cash',
  card: 'Card',
  bank_transfer: 'Bank transfer',
  cheque: 'Cheque',
  gateway: 'Online payment',
}

export interface InvoiceItem {
  id: string
  order_item_id: string | null
  product_id: string
  quantity: number
  unit_price: string
  discount_amount: string
  tax_rate: string
  tax_amount: string
  /**
   * The tax split frozen on the line, named for its shape rather than its country.
   *
   * Two authorities on one supply fill primary and secondary (India's CGST + SGST); one
   * authority fills unified (India's IGST, and every US sale). The slots that do not apply
   * are zero, and which labels the used ones carry is the regime's to say -- ask
   * `RegimeInfo.component_labels`, never the field name.
   */
  primary_tax_amount: string
  secondary_tax_amount: string
  unified_tax_amount: string
  /** HSN or SAC under GST, whatever the operator classifies by in the US. */
  tax_code: string | null
  line_total: string
  /** The rule that priced this line, kept so the screen can answer "why this rate?". */
  tax_rule_id: string | null
  tax_rule_name: string | null
  tax_rule_valid_from: string | null
  product_data: {
    code?: string
    name?: string
    unit_of_measure?: string
    /** Copied verbatim from the order line at invoice time; see PRD 4.1.3. */
    price_anomaly?: boolean
    price_anomaly_reason?: string | null
  }
}

export interface Payment {
  id: string
  invoice_id: string
  customer_id: string
  amount: string
  method: PaymentMethod
  status: 'pending' | 'completed' | 'failed' | 'voided' | 'unapplied'
  reference: string | null
  received_at: string
  received_by: string | null
  gateway: string | null
  gateway_txn_id: string | null
  notes: string | null
  voided_at: string | null
  void_reason: string | null
  created_at: string
}

/**
 * How the supply was sourced: within the seller's state, across a state line, or unknown.
 *
 * Decided from two state codes at the moment the invoice was raised and frozen on it. What
 * it *means* is the jurisdiction's to say and is not written down here: in India the same
 * 18% is CGST 9 + SGST 9 within a state and IGST 18 across a border, and the customer
 * claims the credit under whichever one the document says; in the US the money does not
 * split either way, and the sourcing is recorded for the return rather than for the total.
 * `supplyLabel` in `features/company/regime` puts it into the regime's own words.
 */
export const SUPPLY_TYPES = ['intra_state', 'inter_state', 'none'] as const

export type SupplyType = (typeof SUPPLY_TYPES)[number]

export interface InvoiceSummary {
  id: string
  invoice_number: string
  order_id: string | null
  customer_id: string
  user_id: string
  invoice_date: string
  due_date: string
  status: InvoiceStatus
  subtotal: string
  tax_amount: string
  total: string
  amount_paid: string
  balance_due: string
  supply_type: SupplyType
  created_at: string
}

export interface TaxBand {
  tax_rule_id: string | null
  tax_rule_name: string | null
  valid_from: string | null
  /**
   * The commodity classification the band was charged under -- an HSN under GST, a
   * product code in the US. Bands are grouped by this as well as the rate, because a
   * return is filed rate-wise within a classification. Null on invoices issued before
   * the code was kept, and on lines whose product has none.
   */
  tax_code?: string | null
  rate: string
  taxable: string
  tax: string
}

/** Advice computed on read: this invoice does not look like the customer's usual one. */
export interface InvoiceAnomaly {
  kind: 'amount' | 'tax_rate'
  message: string
  item_id: string | null
  expected: string | null
  actual: string | null
}

export interface Invoice extends InvoiceSummary {
  location_id: string | null
  discount_amount: string
  tax_breakdown: TaxBand[]
  customer_data: Record<string, string | number | boolean | null>
  /** The seller as they were when the document was raised, not as they are now. */
  company_data: Record<string, string | number | boolean | null>
  place_of_supply: string | null
  place_of_supply_code: string | null
  /** Signed, under a rupee either way, and printed as its own line. */
  round_off: string
  /** The order's delivery charge, billed on its first invoice; already inside `total`. */
  shipping_amount: string
  terms: string | null
  notes: string | null
  issued_at: string | null
  paid_at: string | null
  sent_at: string | null
  voided_at: string | null
  void_reason: string | null
  updated_at: string
  items: InvoiceItem[]
  payments: Payment[]
  /** Only on the single-invoice read; absent elsewhere. */
  anomalies?: InvoiceAnomaly[]
}

export interface AgingBucket {
  label: string
  from_days: number | null
  to_days: number | null
  invoice_count: number
  amount: string
}

export interface CustomerAging {
  customer_id: string
  customer_name: string
  buckets: AgingBucket[]
  total: string
}

export interface AgingReport {
  as_of: string
  customers: CustomerAging[]
  totals: AgingBucket[]
  total_outstanding: string
}

export interface InvoiceFilters {
  page?: number
  q?: string
  status?: InvoiceStatus | ''
  customer_id?: string
  order_id?: string
  location_id?: string
  unpaid_only?: boolean
  unapplied_only?: boolean
  date_from?: string
  date_to?: string
}

function query(filters: InvoiceFilters): string {
  const params = new URLSearchParams()
  if (filters.page && filters.page > 1) params.set('page', String(filters.page))
  if (filters.q) params.set('q', filters.q)
  if (filters.status) params.set('status', filters.status)
  if (filters.customer_id) params.set('customer_id', filters.customer_id)
  if (filters.order_id) params.set('order_id', filters.order_id)
  if (filters.location_id) params.set('location_id', filters.location_id)
  if (filters.unpaid_only) params.set('unpaid_only', 'true')
  if (filters.unapplied_only) params.set('unapplied_only', 'true')
  if (filters.date_from) params.set('date_from', filters.date_from)
  if (filters.date_to) params.set('date_to', filters.date_to)
  const qs = params.toString()
  return qs ? `?${qs}` : ''
}

export const listInvoices = (filters: InvoiceFilters = {}) =>
  apiFetch<Page<InvoiceSummary>>(`${API_PREFIX}/invoices${query(filters)}`)

export const exportInvoices = (filters: InvoiceFilters, format: ExportFormat) => {
  const qs = query(filters)
  return apiBlob(`${API_PREFIX}/invoices/export${qs ? `${qs}&fmt=${format}` : `?fmt=${format}`}`)
}

export const getInvoice = (id: string) => apiGet<Invoice>(`${API_PREFIX}/invoices/${id}`)

export const getAging = (asOf?: string) =>
  apiGet<AgingReport>(`${API_PREFIX}/invoices/aging${asOf ? `?as_of=${asOf}` : ''}`)

export interface InvoiceLineInput {
  order_item_id: string
  quantity: number
}

export interface InvoiceInput {
  order_id: string
  due_date?: string | null
  notes?: string | null
  terms?: string | null
  /** Empty means everything still uninvoiced on the order -- see the API docstring. */
  items?: InvoiceLineInput[]
}

export const createInvoice = (input: InvoiceInput) =>
  apiGet<Invoice>(`${API_PREFIX}/invoices`, { method: 'POST', body: JSON.stringify(input) })

export const issueInvoice = (id: string) =>
  apiGet<Invoice>(`${API_PREFIX}/invoices/${id}/issue`, { method: 'POST', body: '{}' })

export const voidInvoice = (id: string, reason: string) =>
  apiGet<Invoice>(`${API_PREFIX}/invoices/${id}/void`, {
    method: 'POST',
    body: JSON.stringify({ reason }),
  })

export const deleteInvoice = (id: string) =>
  apiFetch<void>(`${API_PREFIX}/invoices/${id}`, { method: 'DELETE' })

export interface PaymentInput {
  amount: string
  method: PaymentMethod
  reference?: string | null
  notes?: string | null
}

/**
 * Record a payment. The `Idempotency-Key` is not optional.
 *
 * The key is minted by the caller and reused across retries of the *same* submission, so a
 * tap that times out and is tapped again records one payment rather than two. Generating it
 * inside this function would defeat the point -- every retry would carry a fresh key and
 * the server would treat each as a new payment.
 */
export const recordPayment = (invoiceId: string, input: PaymentInput, idempotencyKey: string) =>
  apiGet<Payment>(`${API_PREFIX}/invoices/${invoiceId}/payments`, {
    method: 'POST',
    body: JSON.stringify(input),
    headers: { 'Idempotency-Key': idempotencyKey },
  })

export const voidPayment = (paymentId: string, reason: string) =>
  apiGet<Payment>(`${API_PREFIX}/invoices/payments/${paymentId}/void`, {
    method: 'POST',
    body: JSON.stringify({ reason }),
  })

/**
 * The stored PDF, as a blob.
 *
 * Fetched rather than linked because the route needs the bearer token, and an `<a href>`
 * cannot carry one. The blob URL the caller builds from this is revoked once the browser
 * has it.
 */
export const fetchInvoicePdf = (id: string) => apiBlob(`${API_PREFIX}/invoices/${id}/pdf`)

/** A key that survives re-renders is the caller's job; this only makes one. */
export const newIdempotencyKey = (): string =>
  typeof crypto !== 'undefined' && 'randomUUID' in crypto
    ? crypto.randomUUID()
    : `${Date.now()}-${Math.random().toString(16).slice(2)}`

/** Rupees, en-IN grouping. Lives in `@/lib/money` so every screen prints one format. */
export { formatMoney } from '@/lib/money'

/** Negative means "not due yet". Used for the overdue notice on the detail screen. */
export function daysPastDue(dueDate: string, now = new Date()): number {
  const due = new Date(`${dueDate}T00:00:00`)
  return Math.floor((now.getTime() - due.getTime()) / 86_400_000)
}
